import React, { useEffect, useState } from "react";
import classNames from "classnames";
import { Helmet } from "react-helmet";
import Grid from "@material-ui/core/Grid";
import Paper from "@material-ui/core/Paper";
import IconButton from "@material-ui/core/IconButton";
import Checkbox from "@material-ui/core/Checkbox";
import Tooltip from "@material-ui/core/Tooltip";
import { withStyles } from "@material-ui/core/styles";
import createStyles from "@material-ui/core/styles/createStyles";
import RefreshIcon from "@material-ui/icons/Refresh";
import FolderIcon from "@material-ui/icons/Folder";
import InsertDriveFileIcon from "@material-ui/icons/InsertDriveFile";
import HomeIcon from "@material-ui/icons/Home";
import PermMediaOutlinedIcon from "@material-ui/icons/PermMediaOutlined";
import UiButton from "../../common/UiButton";
import UiInput from "../../common/UiInput";
import UiProgress from "../../common/UiProgess";
import UiFormTitle from "../../common/UiFormTitle";
import t from "../../../locales/t";

const normalizePath = (path: string) => path.replace(/\/+$/, "");

const getDisplayName = (name: string) => {
  const normalized = normalizePath(name);
  const segments = normalized.split("/");
  return segments[segments.length - 1] || normalized;
};

const selectedCardBackground = "rgba(41, 72, 255, 0.05)";

const getBrowseItemCardStyle = (selected: boolean) => ({
  position: "relative" as const,
  border: "1px solid rgba(72, 72, 72, 0.2)",
  borderRadius: 6,
  backgroundColor: selected ? selectedCardBackground : "#fff",
  padding: "4px 6px 8px",
  height: "100%",
  boxSizing: "border-box" as const,
});

const browseItemCheckboxStyle = {
  position: "absolute" as const,
  top: 2,
  left: 2,
  padding: 0,
};

const browseItemContentStyle = {
  display: "flex",
  flexDirection: "column" as const,
  alignItems: "center",
  textAlign: "center" as const,
  paddingTop: 4,
};

const browseItemNameStyle = {
  marginTop: 4,
  fontSize: 12,
  lineHeight: 1.2,
  width: "100%",
  minWidth: 0,
  overflow: "hidden" as const,
  textOverflow: "ellipsis" as const,
  whiteSpace: "nowrap" as const,
};

const browseFolderFileCountStyle = {
  position: "absolute" as const,
  bottom: 4,
  right: 6,
  display: "flex",
  alignItems: "center",
  gap: 2,
  fontSize: 11,
  lineHeight: 1,
  color: "rgb(66, 65, 65)",
};

const browseStatusBarStyle = {
  marginTop: 16,
  padding: "0px 2px",
  borderRadius: 6,
  border: "1px solid rgba(72, 72, 72, 0.2)",
  backgroundColor: "rgba(72, 72, 72, 0.04)",
  fontSize: 14,
  display: "flex",
  alignItems: "center",
  gap: 2,
};

const browseStatusBarCheckboxStyle = {
  padding: 0,
  marginRight: 4,
};

const formatCountLabel = (
  count: number,
  singular: string,
  plural: string,
  selected: boolean,
) => {
  const label = count > 1 ? plural : singular;
  return `${count} ${label}${selected ? " sélectionné" : ""}${selected && count > 1 ? "s" : ""}`;
};

const browseEmptyStateStyle = {
  display: "flex",
  flexDirection: "column" as const,
  alignItems: "center",
  justifyContent: "center",
  width: "100%",
  minHeight: 200,
  gap: 8,
  textAlign: "center" as const,
};

const browseFileManagerContainerStyle = {
  minHeight: 360,
  display: "flex",
  flexDirection: "column" as const,
};

const browseContentAreaStyle = {
  marginTop: 16,
  minHeight: 240,
  flex: 1,
};

const browseSkeletonPulseStyle = {
  backgroundColor: "rgba(72, 72, 72, 0.1)",
  borderRadius: 4,
  animation: "browseSkeletonPulse 1.5s ease-in-out infinite",
};

const BROWSE_SKELETON_COUNT = 12;

const BrowseSkeletonGrid = () => (
  <>
    {Array.from({ length: BROWSE_SKELETON_COUNT }, (_, index) => (
      <Grid
        item
        xs={4}
        sm={3}
        md={2}
        key={`browse-skeleton-${index}`}
        style={{ height: "100%" }}
      >
        <div style={{ ...getBrowseItemCardStyle(false), pointerEvents: "none" }}>
          <div style={{ ...browseItemContentStyle, paddingTop: 20 }}>
            <div style={{ ...browseSkeletonPulseStyle, width: 32, height: 32 }} />
            <div
              style={{
                ...browseSkeletonPulseStyle,
                width: "80%",
                height: 12,
                marginTop: 8,
              }}
            />
          </div>
        </div>
      </Grid>
    ))}
  </>
);

const importVideoTooltipStyles = () =>
  createStyles({
    tooltip: {
      fontSize: 14,
      lineHeight: 1.43,
      maxWidth: 520,
    },
  });

const ImportVideoTooltipBase = (props: any) => {
  const { classes, children, ...rest } = props;
  return (
    <Tooltip classes={{ tooltip: classes.tooltip }} {...rest}>
      {children}
    </Tooltip>
  );
};

const ImportVideoTooltip = withStyles(importVideoTooltipStyles)(
  ImportVideoTooltipBase,
);

const ImportVideoForm = (props: any) => {
  const {
    classes,
    history,
    project,
    listVideoFiles,
    dispatchVideos,
  } = props;

  const [datas, setDatas] = useState<any>({});
  const [error, setError] = useState<string>("");
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [dispatched, setDispatched] = useState<boolean>(false);
  const [browsePath, setBrowsePath] = useState<string>("");
  const [baseUploadFolder, setBaseUploadFolder] = useState<string>("");
  const [folderEntries, setFolderEntries] = useState<string[]>([]);
  const [fileEntries, setFileEntries] = useState<string[]>([]);
  const [selectedVideos, setSelectedVideos] = useState<string[]>([]);
  const [selectedFolders, setSelectedFolders] = useState<string[]>([]);
  const [folderFileCounts, setFolderFileCounts] = useState<
    Record<string, number>
  >({});
  const [loadingBrowse, setLoadingBrowse] = useState<boolean>(false);

  useEffect(() => {
    setDatas((d: any) => ({ ...d, projectId: project.id }));
  }, [project]);

  useEffect(() => {
    if (!project.name) {
      return;
    }
    setBrowsePath(project.name);
    setBaseUploadFolder(project.name);
    setSelectedVideos([]);
    setSelectedFolders([]);
    setFolderFileCounts({});
  }, [project.name]);

  const getVideoRelativePath = (folderPath: string, fileName: string) => {
    const normalizedFolderPath = normalizePath(folderPath);
    if (!normalizedFolderPath) {
      return fileName;
    }

    const normalizedBase = normalizePath(baseUploadFolder);

    if (!normalizedBase || normalizedFolderPath === normalizedBase) {
      return fileName;
    }

    if (normalizedFolderPath.startsWith(`${normalizedBase}/`)) {
      const relativeFolder = normalizedFolderPath.slice(normalizedBase.length + 1);
      return relativeFolder ? `${relativeFolder}/${fileName}` : fileName;
    }

    return `${normalizedFolderPath}/${fileName}`;
  };

  const getUploadFolderForDispatch = () => {
    if (baseUploadFolder) {
      return normalizePath(baseUploadFolder);
    }

    if (selectedFolders.length > 0) {
      return normalizePath(selectedFolders[0]).split("/")[0];
    }

    return "";
  };

  const fetchBrowseEntries = async () => {
    if (!project.name) {
      return;
    }

    try {
      setLoadingBrowse(true);
      const normalizedBrowsePath = normalizePath(browsePath) || project.name;
      const data = await listVideoFiles({
        uploadFolder: normalizedBrowsePath,
      });

      setFolderEntries(data?.folders ?? []);
      setFileEntries(data?.files ?? []);
    } catch (e) {
      setError("Impossible de récupérer la liste des dossiers et fichiers");
    } finally {
      setLoadingBrowse(false);
    }
  };

  useEffect(() => {
    fetchBrowseEntries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [browsePath, project.name]);

  useEffect(() => {
    if (!submitting) {
      return;
    }
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [submitting]);

  const handleChange = (e: any) => {
    if (error) {
      setError("");
    }
    setDatas({
      ...datas,
      [e.target.name]: e.target.value,
    });
  };

  const openFolder = (folderName: string) => {
    if (error) {
      setError("");
    }

    const normalizedFolder = normalizePath(folderName);
    const nextPath = browsePath
      ? `${normalizePath(browsePath)}/${normalizedFolder}`
      : normalizedFolder;

    setBrowsePath(nextPath);
  };

  const navigateBrowsePath = (targetPath: string) => {
    if (error) {
      setError("");
    }
    const normalized = normalizePath(targetPath);
    if (!normalized || !project.name) {
      setBrowsePath(project.name ?? "");
      setBaseUploadFolder(project.name ?? "");
      return;
    }
    setBrowsePath(normalized);
  };

  const browsePathSegments = browsePath
    ? normalizePath(browsePath).split("/").filter(Boolean)
    : [];

  const getFolderPath = (folderName: string) => {
    const normalizedFolder = normalizePath(folderName);
    if (!browsePath) {
      return normalizedFolder;
    }
    return `${normalizePath(browsePath)}/${normalizedFolder}`;
  };

  const getVideoPathsForFolder = (folderPath: string, files: string[]) =>
    files.map((file) => getVideoRelativePath(folderPath, file));

  const clearFolderFileCount = (folderPath: string) => {
    setFolderFileCounts((prev) => {
      const next = { ...prev };
      delete next[folderPath];
      return next;
    });
  };

  const loadFolderFiles = async (folderPath: string) => {
    const data = await listVideoFiles({ uploadFolder: folderPath });
    const files = data?.files ?? [];
    setFolderFileCounts((prev) => ({
      ...prev,
      [folderPath]: files.length,
    }));
    return files;
  };

  const toggleVideoSelection = (videoPath: string) => {
    if (error) {
      setError("");
    }

    setSelectedVideos((prev) =>
      prev.includes(videoPath)
        ? prev.filter((video) => video !== videoPath)
        : [...prev, videoPath],
    );
  };

  const toggleFileSelection = (fileName: string) => {
    toggleVideoSelection(getVideoRelativePath(browsePath, fileName));
  };

  const toggleFolderSelection = async (folderName: string) => {
    if (!project.name) {
      return;
    }

    const folderPath = getFolderPath(folderName);

    if (selectedFolders.includes(folderPath)) {
      setSelectedFolders((prev) => prev.filter((path) => path !== folderPath));
      clearFolderFileCount(folderPath);

      try {
        const data = await listVideoFiles({ uploadFolder: folderPath });
        const videoPaths = getVideoPathsForFolder(
          folderPath,
          data?.files ?? [],
        );
        setSelectedVideos((prev) =>
          prev.filter((video) => !videoPaths.includes(video)),
        );
      } catch (e) {
        setError("Impossible de désélectionner les vidéos du dossier");
      }
      return;
    }

    setSelectedFolders((prev) =>
      prev.includes(folderPath) ? prev : [...prev, folderPath],
    );

    try {
      const files = await loadFolderFiles(folderPath);
      const videoPaths = getVideoPathsForFolder(folderPath, files);

      setSelectedVideos((prev) => {
        const next = [...prev];
        videoPaths.forEach((video) => {
          if (!next.includes(video)) {
            next.push(video);
          }
        });
        return next;
      });
    } catch (e) {
      setSelectedFolders((prev) => prev.filter((path) => path !== folderPath));
      clearFolderFileCount(folderPath);
      setError("Impossible de sélectionner les vidéos du dossier");
    }
  };

  const isFolderSelected = (folderName: string) =>
    selectedFolders.includes(getFolderPath(folderName));

  const visibleFolderPaths = folderEntries.map((folder) => getFolderPath(folder));
  const visibleFilePaths = fileEntries.map((file) =>
    getVideoRelativePath(browsePath, file),
  );
  const hasVisibleEntries =
    folderEntries.length > 0 || fileEntries.length > 0;
  const isProjectFolderEmpty =
    !loadingBrowse &&
    normalizePath(browsePath) === normalizePath(project.name) &&
    !hasVisibleEntries;
  const areAllVisibleSelected =
    hasVisibleEntries &&
    folderEntries.every((folder) => isFolderSelected(folder)) &&
    fileEntries.every((file) =>
      selectedVideos.includes(getVideoRelativePath(browsePath, file)),
    );
  const hasSomeVisibleSelected =
    folderEntries.some((folder) => isFolderSelected(folder)) ||
    fileEntries.some((file) =>
      selectedVideos.includes(getVideoRelativePath(browsePath, file)),
    );

  const toggleSelectAll = async () => {
    if (!project.name || !hasVisibleEntries) {
      return;
    }

    if (error) {
      setError("");
    }

    if (areAllVisibleSelected) {
      setSelectedFolders((prev) =>
        prev.filter((path) => !visibleFolderPaths.includes(path)),
      );
      setSelectedVideos((prev) =>
        prev.filter((video) => !visibleFilePaths.includes(video)),
      );
      setFolderFileCounts((prev) => {
        const next = { ...prev };
        visibleFolderPaths.forEach((path) => {
          delete next[path];
        });
        return next;
      });

      try {
        const folderVideoPaths = await Promise.all(
          folderEntries.map(async (folder) => {
            const folderPath = getFolderPath(folder);
            const data = await listVideoFiles({ uploadFolder: folderPath });
            return getVideoPathsForFolder(folderPath, data?.files ?? []);
          }),
        );
        const videosToRemove = folderVideoPaths.flat();
        setSelectedVideos((prev) =>
          prev.filter((video) => !videosToRemove.includes(video)),
        );
      } catch (e) {
        setError("Impossible de désélectionner tous les éléments");
      }
      return;
    }

    const foldersToSelect = folderEntries
      .map((folder) => getFolderPath(folder))
      .filter((folderPath) => !selectedFolders.includes(folderPath));

    setSelectedFolders((prev) => {
      const next = [...prev];
      foldersToSelect.forEach((folderPath) => {
        if (!next.includes(folderPath)) {
          next.push(folderPath);
        }
      });
      return next;
    });

    try {
      const folderResults = await Promise.all(
        folderEntries.map(async (folder) => {
          const folderPath = getFolderPath(folder);
          if (selectedFolders.includes(folderPath)) {
            return { folderPath, videoPaths: [] as string[] };
          }

          const files = await loadFolderFiles(folderPath);

          return {
            folderPath,
            videoPaths: getVideoPathsForFolder(folderPath, files),
          };
        }),
      );
      setSelectedVideos((prev) => {
        const next = [...prev];
        visibleFilePaths.forEach((video) => {
          if (!next.includes(video)) {
            next.push(video);
          }
        });
        folderResults.forEach(({ videoPaths }) => {
          videoPaths.forEach((video) => {
            if (!next.includes(video)) {
              next.push(video);
            }
          });
        });
        return next;
      });
    } catch (e) {
      setSelectedFolders((prev) =>
        prev.filter((path) => !foldersToSelect.includes(path)),
      );
      foldersToSelect.forEach((folderPath) => clearFolderFileCount(folderPath));
      setError("Impossible de sélectionner tous les éléments");
    }
  };

  const selectedFoldersCount = folderEntries.filter((folder) =>
    isFolderSelected(folder),
  ).length;
  const hasSelection = selectedFoldersCount > 0 || selectedVideos.length > 0;
  const statusLabel = hasSelection
    ? [
        selectedFoldersCount > 0
          ? formatCountLabel(
              selectedFoldersCount,
              "dossier",
              "dossiers",
              true,
            )
          : null,
        selectedVideos.length > 0
          ? formatCountLabel(
              selectedVideos.length,
              "fichier",
              "fichiers",
              true,
            )
          : null,
      ]
        .filter(Boolean)
        .join(", ")
    : `${formatCountLabel(
        folderEntries.length,
        "dossier",
        "dossiers",
        false,
      )}, ${formatCountLabel(fileEntries.length, "fichier", "fichiers", false)}`;

  const submit = async (e: any) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    const { subFolderName, fps } = datas;

    try {
      const payload = {
        projectName: project.name,
        // uploadFolder: getUploadFolderForDispatch(),
        targetFolder: subFolderName,
        fps: parseFloat(fps),
        // videoNames: [...selectedFolders, ...selectedVideos],
        videoNames: [...selectedVideos],
      };      
      await dispatchVideos(payload);
      // await new Promise((resolve) => setTimeout(resolve, 2000));

      setDispatched(true);
      setTimeout(() => history.push("/projectsOperations"), 2000);
    } catch (err) {
      setSubmitting(false);
      console.error(err);
      setError(
        // @ts-ignore
        (err?.response?.data?.errorKey
          ? // @ts-ignore
            t("error." + err?.response?.data?.errorKey, {
              folderName: subFolderName,
            })
          : // @ts-ignore
            err?.response?.data?.message) ?? "Une erreur est survenue",
      );
    }
  };

  return (
    <Grid
      container
      direction="row"
      justify="center"
      alignItems="flex-start"
      className={classes.root}
    >
      <Helmet>
        <title>Importer des vidéos</title>
      </Helmet>
      <Grid item xs={12} sm={8}>
        <Paper className={classes.paper}>
          <UiFormTitle>{`Importer les vidéos ${
            project.name ? `pour le projet ${project.name}` : ""
          }`}</UiFormTitle>
          <form onSubmit={submit} id="form">
            <Grid
              container
              spacing={16}
              className={classes.form}
              style={{ paddingBottom: 16 }}
            >
              <Grid item xs={12} sm={6}>
                <UiInput
                  label="Dossier de stockage"
                  autoFocus
                  name="subFolderName"
                  required
                  onChange={handleChange}
                  value={datas.subFolderName ?? ""}
                  tabIndex={2}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <UiInput
                  label="Fréquence d'images (FPS)"
                  name="fps"
                  type="number"
                  required
                  onChange={handleChange}
                  value={datas.fps ?? ""}
                  tabIndex={3}
                />
              </Grid>

              {!(submitting || dispatched) && (
              <Grid item xs={12}>
                <div style={browseFileManagerContainerStyle}>
                <style>
                  {`@keyframes browseSkeletonPulse {
                    0% { opacity: 1; }
                    50% { opacity: 0.45; }
                    100% { opacity: 1; }
                  }`}
                </style>
                <label className={classes.label}>
                  Dossier et fichiers d&apos;upload
                </label>

                <div style={browseStatusBarStyle}>
                  <ImportVideoTooltip
                    title={
                      areAllVisibleSelected
                        ? "Tout désélectionner"
                        : "Tout sélectionner"
                    }
                  >
                    <Checkbox
                      checked={areAllVisibleSelected}
                      indeterminate={
                        hasSomeVisibleSelected && !areAllVisibleSelected
                      }
                      onChange={toggleSelectAll}
                      disabled={loadingBrowse || !hasVisibleEntries}
                      color="secondary"
                      style={browseStatusBarCheckboxStyle}
                    />
                  </ImportVideoTooltip>
                  <span style={{ flex: 1, display: "flex", alignItems: "center" }}>
                    {loadingBrowse ? (
                      <span
                        style={{
                          ...browseSkeletonPulseStyle,
                          display: "inline-block",
                          width: 180,
                          height: 14,
                        }}
                      />
                    ) : (
                      statusLabel
                    )}
                  </span>
                  <ImportVideoTooltip title="Rafraîchir la liste">
                    <span>
                      <IconButton
                        onClick={fetchBrowseEntries}
                        disabled={loadingBrowse}
                        style={{ padding: 6 }}
                      >
                        <RefreshIcon style={{ fontSize: 18 }} />
                      </IconButton>
                    </span>
                  </ImportVideoTooltip>
                </div>

                {/* BROWSE PATH */}
                <nav
                  aria-label="Chemin du dossier"
                  style={{
                    marginTop: 8,
                    fontSize: 14,
                    display: "flex",
                    alignItems: "center",
                    flexWrap: "wrap",
                    rowGap: 4,
                    columnGap: 2,
                  }}
                >
                  <ImportVideoTooltip title={"Racine du dossier d'upload"}>
                    <IconButton
                      type="button"
                      onClick={() => navigateBrowsePath(project.name)}
                      style={{ padding: 4 }}
                      aria-label="Racine"
                    >
                      <HomeIcon
                        style={{
                          fontSize: 18,
                          color:
                            normalizePath(browsePath) !==
                            normalizePath(project.name)
                              ? "rgb(66, 65, 65)"
                              : "#9e9e9e",
                        }}
                      />
                    </IconButton>
                  </ImportVideoTooltip>
                  {browsePathSegments.map((segment, index) => {
                    const pathUpTo = browsePathSegments
                      .slice(0, index + 1)
                      .join("/");
                    const isLast = index === browsePathSegments.length - 1;
                    return (
                      <React.Fragment key={pathUpTo}>
                        <span
                          aria-hidden="true"
                          role="separator"
                          style={{
                            color: "#9e9e9e",
                            userSelect: "none",
                            padding: "0 2px",
                          }}
                        >
                          /
                        </span>
                        {isLast ? (
                          <span
                            style={{
                              color: "rgb(66, 65, 65)",
                              fontWeight: 600,
                              padding: "2px 4px",
                            }}
                          >
                            {segment}
                          </span>
                        ) : (
                          <ImportVideoTooltip title={`Aller dans ${pathUpTo}`}>
                            <button
                              type="button"
                              className={classes.browseCrumbLink}
                              onClick={() => navigateBrowsePath(pathUpTo)}
                            >
                              {segment}
                            </button>
                          </ImportVideoTooltip>
                        )}
                      </React.Fragment>
                    );
                  })}
                </nav>

                <Grid container spacing={8} style={browseContentAreaStyle}>
                    {loadingBrowse ? (
                      <BrowseSkeletonGrid />
                    ) : folderEntries.length === 0 && fileEntries.length === 0 ? (
                      <Grid item xs={12}>
                        <div style={browseEmptyStateStyle}>
                          <PermMediaOutlinedIcon
                            style={{ fontSize: 64, color: "rgb(66, 65, 65)" }}
                          />
                          {isProjectFolderEmpty ? (
                            <>
                              <div
                                style={{
                                  color: "rgb(66, 65, 65)",
                                  fontWeight: 600,
                                }}
                              >
                                Pas de média
                              </div>
                              <div style={{ color: "rgb(66, 65, 65)", fontSize: 14, fontStyle: "italic" }}>
                                Veuillez ajouter des médias ou créer un dossier dans la bucket
                                d&apos;entrée
                              </div>
                            </>
                          ) : (
                            <div style={{ color: "rgb(66, 65, 65)" }}>
                              Aucun dossier ou fichier trouvé
                            </div>
                          )}
                        </div>
                      </Grid>
                    ) : (
                      <>
                        {folderEntries.map((folder) => {
                      const folderPath = getFolderPath(folder);
                      const folderSelected = isFolderSelected(folder);
                      const folderFileCount = folderFileCounts[folderPath];

                      return (
                      <Grid item xs={4} sm={3} md={2} key={`folder-${folder}`} style={{ height: '100%' }}>
                        <div
                          style={getBrowseItemCardStyle(folderSelected)}
                        >
                          <Checkbox
                            checked={folderSelected}
                            onChange={() => toggleFolderSelection(folder)}
                            color="secondary"
                            style={browseItemCheckboxStyle}
                          />                          
                          <button
                            type="button"
                            onClick={() => openFolder(folder)}
                            style={{
                              ...browseItemContentStyle,
                              border: "none",
                              background: "transparent",
                              cursor: "pointer",
                              width: "100%",
                              color: "inherit",
                            }}
                          >
                            <FolderIcon
                              style={{ fontSize: 32 }}
                              color="primary"
                            />
                            <ImportVideoTooltip title={getDisplayName(folder)}>
                              <div style={browseItemNameStyle}>
                                {getDisplayName(folder)}
                              </div>
                            </ImportVideoTooltip>
                          </button>
                          {folderSelected && folderFileCount !== undefined && (
                            <div style={browseFolderFileCountStyle}>
                              <span>{folderFileCount}</span>
                              <InsertDriveFileIcon
                                style={{ fontSize: 14, color: "#90a4ae" }}
                              />
                            </div>
                          )}
                        </div>
                      </Grid>
                      );
                    })}
                    {fileEntries.map((file) => {
                      const videoPath = getVideoRelativePath(browsePath, file);
                      const isSelected = selectedVideos.includes(videoPath);
                      return (
                        <Grid item xs={4} sm={3} md={2} key={`file-${file}`} style={{ height: '100%' }}>
                          <div style={getBrowseItemCardStyle(isSelected)}>
                            <Checkbox
                              checked={isSelected}
                              onChange={() => toggleFileSelection(file)}
                              color="secondary"
                              style={browseItemCheckboxStyle}
                            />
                            <div style={browseItemContentStyle}>
                              <InsertDriveFileIcon
                                style={{ fontSize: 32, color: "#90a4ae" }}
                              />
                              <ImportVideoTooltip title={getDisplayName(file)}>
                                <div style={browseItemNameStyle}>
                                  {getDisplayName(file)}
                                </div>
                              </ImportVideoTooltip>
                            </div>
                          </div>
                        </Grid>
                      );
                    })}
                      </>
                    )}
                </Grid>

                </div>
              </Grid>
              )}

              {(submitting || dispatched) && (
                <Grid item xs={12}>
                  <Grid
                    container
                    className={classNames(
                      classes.dropContainer,
                      classes.uploading,
                    )}
                    alignItems="center"
                    justify="center"
                  >
                    <Grid item xs={12} sm={8}>
                      <UiProgress
                        variant={dispatched ? "determinate" : "indeterminate"}
                        color="secondary"
                        value={dispatched ? 100 : undefined}
                      />
                      {dispatched ? (
                        <small>
                          Dispatch lancé, redirection en cours...
                        </small>
                      ) : (
                        <>
                          <small>Dispatch des vidéos en cours...</small>
                          <br />
                          <small
                            style={{ fontStyle: "italic", fontSize: "16px" }}
                          >
                            Veuillez ne pas fermer la page
                          </small>
                        </>
                      )}
                    </Grid>
                  </Grid>
                </Grid>
              )}

              <Grid item xs={12}>
                <Grid
                  container
                  direction="row"
                  justify="space-between"
                  spacing={16}
                >
                  <Grid item xs={12} sm={8}>
                    {error !== "" && (
                      <div style={{ marginTop: 16 }}>
                        <span style={{ color: "red" }}>Erreur : {error}</span>
                      </div>
                    )}
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <ImportVideoTooltip
                      title={
                        submitting
                          ? "Import des vidéos en cours..."
                          : "Importer les vidéos sélectionnées"
                      }
                    >
                      <span style={{ display: "block", width: "100%" }}>
                        <UiButton
                          style={{ marginTop: 16 }}
                          fullWidth
                          color="secondary"
                          variant="contained"
                          type="submit"
                          tabIndex={3}
                          disabled={
                            submitting ||
                            dispatched ||
                            error !== "" ||
                            !getUploadFolderForDispatch() ||
                            selectedVideos.length === 0 ||
                            !datas.subFolderName ||
                            !datas.fps ||
                            !Number.isFinite(parseFloat(datas.fps)) ||
                            parseFloat(datas.fps) <= 0
                          }
                        >
                          {submitting
                            ? "Import des vidéos en cours..."
                            : "Importer vos vidéos"}
                        </UiButton>
                      </span>
                    </ImportVideoTooltip>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </form>
        </Paper>
      </Grid>
    </Grid>
  );
};

export default ImportVideoForm;
